import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { tg } from "../telegram";
import { ru } from "./ru";
import { en } from "./en";
import { fa } from "./fa";

export type Lang = "ru" | "en" | "fa";
export const SUPPORTED: Lang[] = ["en", "ru", "fa"];

const CATALOGS: Record<Lang, Record<string, string>> = { ru, en, fa };

// Languages that need RTL layout direction.
const RTL_LANGS = new Set<Lang>(["fa"]);

export function isRtl(lang: Lang): boolean {
  return RTL_LANGS.has(lang);
}

export function normalizeLang(code: string | undefined | null): Lang {
  if (!code) return "en";
  const base = code.toLowerCase().replace("_", "-").split("-")[0];
  return (SUPPORTED.includes(base as Lang) ? base : "en") as Lang;
}

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = "energo.lang";

interface ProviderProps {
  children: React.ReactNode;
  /** Server-side preference (from /api/me). When present, wins over localStorage on first load. */
  initialLang?: Lang | null;
}

export function I18nProvider({ children, initialLang }: ProviderProps) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (initialLang) return initialLang;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored as Lang)) return stored as Lang;
    } catch {}
    // Fall back to Telegram's user.language_code for the first paint.
    const tgLang = tg()?.initDataUnsafe?.user?.language_code;
    if (tgLang) return normalizeLang(tgLang);
    return "en";
  });

  // Adopt server-provided language once it loads (e.g. /api/me).
  useEffect(() => {
    if (initialLang && initialLang !== lang) {
      setLangState(initialLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLang]);

  // Sync <html lang> + <html dir> on each language change.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = isRtl(lang) ? "rtl" : "ltr";
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch {}
  }, []);

  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    const catalog = CATALOGS[lang] || CATALOGS.en;
    let template = catalog[key];
    if (template === undefined) template = CATALOGS.en[key];
    if (template === undefined) return key;
    if (vars) {
      for (const k of Object.keys(vars)) {
        template = template.replaceAll(`{${k}}`, String(vars[k]));
      }
    }
    return template;
  }, [lang]);

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used inside <I18nProvider>");
  return ctx;
}

export const LANG_LABELS: Record<Lang, { label: string; emoji: string }> = {
  ru: { label: "Русский", emoji: "🇷🇺" },
  en: { label: "English", emoji: "🇬🇧" },
  fa: { label: "فارسی", emoji: "🇮🇷" },
};
